/**
 * 航段费用明细下钻抽屉 — 结算账单数据可信化(录入员侧)S4
 *
 * 任务承接:
 *   - T21 单元格下钻入口(LegCostManagementPage 单元格 onClick 打开本抽屉)
 *   - T22 下钻表格(DIRECT + ALLOCATABLE 两段表)
 *   - T23 "打开原件"按钮(走 T19 签名 URL)
 *   - T24 冻结对比顶部提示(差额不一致时跳"批次管理页"重算,不在本抽屉内提供重算入口)
 *   - T26 实时合计为 0 时的提示
 *   - T26b "删除该条"动作(软删除,二次确认 + 必填理由)
 *
 * 数据源:
 *   - GET /v3/settlement/leg-costs/{flight_operation_id}/details/{cost_type}?batch_id=
 *     返回 { flight_info, live_total, frozen_comparison?, direct_items, allocated_items }
 *
 * 设计取舍:
 *   - 之前(2026-05 前)本抽屉内嵌"立即重算批次"按钮(原 T24a 实现),用户反馈"职责越位 —
 *     在查费用明细的页面突然让用户重算整个批次,跨抽象层级",已迁移到"批次管理"专属页面。
 *     本抽屉只保留"差额提示 Alert + 跳链接",不再持有重算动作。
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../hooks/useAuth";
import {
    Drawer,
    Table,
    Tag,
    Space,
    Typography,
    message,
    Alert,
    Empty,
    Button,
    Modal,
    Input,
    Statistic,
    Row,
    Col,
    Divider,
    Spin,
    Tooltip,
} from "antd";
import {
    FilePdfOutlined,
    DeleteOutlined,
    InfoCircleOutlined,
    DollarOutlined,
    PlusOutlined,
    HistoryOutlined,
} from "@ant-design/icons";
import { Segmented } from "antd";
import settlementApi from "../../../../api/settlementApi";

const { Text } = Typography;
const { TextArea } = Input;

const ALLOCATION_COLORS = { DIRECT: "blue", ALLOCATABLE: "purple" };
const COST_TYPE_DISPLAY = {
    FUEL: "Fuel",
    GROUND_HANDLING: "Ground Handling",
    LANDING_PARKING: "Landing/Parking",
    NAVIGATION: "Navigation",
    CATERING: "Catering",
    TRANSPORTATION: "Transportation",
    DEICING: "Deicing",
    POSITIONING: "Positioning",
    LAUNDRY: "Laundry",
    ACCOMMODATION: "Accommodation",
    DISINFECTION: "Disinfection",
    FLIGHT_SERVICE_CHARGE: "Flight Service Charge",
    ULD: "ULD",
    TRAFFIC_RIGHTS_PERMIT: "Traffic Rights Permit",
    SITA: "SITA",
    COMMISSION: "Commission",
    ACMI: "ACMI",
    TRAFFIC_RIGHTS: "Traffic Rights",
    OTHERS_FEE: "Others",
    CARGO_HANDLING: "Cargo Handling",
    BH: "BH (Block Hours)",
    FH: "FH (Flight Hours)",
    TRUCKING_FEE: "Trucking Fee",
};

const formatMoney = (v) =>
    `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// 手工录入族 source_type —— 这些行没有对应的供应商账单原件,单条删除就是它的正常修正方式。
// 其余来源(INVOICE_UPLOAD / EMAIL / OFFLINE 等)背后有真实账单文件,单条删除会让系统记录
// 与账单原件不一致,删除确认框需要额外提示(2026-07:软删行曾阻塞整单"作废并重发")。
const MANUAL_SOURCE_TYPES = new Set(["MANUAL_ENTRY", "ACMI_ENTRY", "COMMISSION_ENTRY"]);
const isBillBackedRecord = (record) => !!record?.source_type && !MANUAL_SOURCE_TYPES.has(record.source_type);

const LegCostDrillDownDrawer = ({
    visible,
    flightOperationId,
    costType,
    batchId,
    onClose,
    onAddCost,
    onAfterDelete,
}) => {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const canOpenAdjustment = hasPermission("settlement:acmi");
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [previewing, setPreviewing] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteReason, setDeleteReason] = useState("");
    const [deleting, setDeleting] = useState(false);

    // S5 / T54 — 看历史档案 + 版本切换
    const [snapshotMode, setSnapshotMode] = useState(false);
    const [snapshotVersions, setSnapshotVersions] = useState([]);
    const [snapshotVersion, setSnapshotVersion] = useState(null);
    const [snapshotData, setSnapshotData] = useState(null);
    const [snapshotLoading, setSnapshotLoading] = useState(false);

    const loadDetails = useCallback(async () => {
        if (!visible || !flightOperationId || !costType) return;
        setLoading(true);
        try {
            const res = await settlementApi.dataQuality.getLegCostDetails(
                flightOperationId,
                costType,
                { batchId: batchId || undefined }
            );
            setData(res.data);
        } catch (err) {
            console.error("加载航段费用明细失败:", err);
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : detail?.message || err.message;
            message.error("加载明细失败: " + msg);
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [visible, flightOperationId, costType, batchId]);

    useEffect(() => {
        if (visible) {
            loadDetails();
        } else {
            // 抽屉关闭时清掉 state,避免下次打开闪一下旧数据
            setData(null);
            setDeleteTarget(null);
            setDeleteReason("");
            setSnapshotMode(false);
            setSnapshotVersions([]);
            setSnapshotVersion(null);
            setSnapshotData(null);
        }
    }, [visible, loadDetails]);

    // S5 / T54 — 加载快照版本列表
    const loadSnapshotVersions = useCallback(async () => {
        if (!batchId) return;
        try {
            const res = await settlementApi.dataQuality.listProfitReportSnapshotVersions(batchId);
            const versions = res.data || [];
            setSnapshotVersions(versions);
            if (versions.length > 0 && !snapshotVersion) {
                setSnapshotVersion(versions[versions.length - 1].version);
            }
        } catch (err) {
            console.error("加载档案版本列表失败:", err);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [batchId, snapshotVersion]);

    // S5 / T54 — 加载某版本快照
    const loadSnapshot = useCallback(
        async (version) => {
            if (!batchId || !flightOperationId || !costType) return;
            setSnapshotLoading(true);
            try {
                const res = await settlementApi.dataQuality.getProfitReportSnapshot(
                    batchId,
                    flightOperationId,
                    costType,
                    version
                );
                setSnapshotData(res.data);
            } catch (err) {
                const detail = err?.response?.data?.detail;
                const msg = typeof detail === "string" ? detail : detail?.message || err.message;
                message.error("加载历史档案失败: " + msg);
                setSnapshotData(null);
            } finally {
                setSnapshotLoading(false);
            }
        },
        [batchId, flightOperationId, costType]
    );

    useEffect(() => {
        if (snapshotMode && batchId) {
            loadSnapshotVersions();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [snapshotMode, batchId]);

    useEffect(() => {
        if (snapshotMode && snapshotVersion) {
            loadSnapshot(snapshotVersion);
        }
    }, [snapshotMode, snapshotVersion, loadSnapshot]);

    // T23 — 打开原件(走 T19 签名 URL)
    const handlePreviewOriginal = useCallback(async (record) => {
        if (!record?.invoice_line_item_id) return;
        setPreviewing(record.invoice_line_item_id);
        try {
            const res = await settlementApi.dataQuality.getInvoiceLineItemPreviewUrl(
                record.invoice_line_item_id
            );
            const url = res.data?.url;
            if (!url) {
                message.error("生成原件签名 URL 失败");
                return;
            }
            window.open(url, "_blank", "noopener,noreferrer");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const errorCode = typeof detail === "object" ? detail?.error_code : undefined;
            if (errorCode === "RAW_FILE_NOT_ARCHIVED") {
                message.warning(
                    "该账单原件已丢失,不可查阅 — 可能是 S2 P0 上线前的历史账单或 AP 邮件路径未存档。"
                );
            } else {
                const msg = typeof detail === "string" ? detail : detail?.message || err.message;
                message.error("打开原件失败: " + msg);
            }
        } finally {
            setPreviewing(null);
        }
    }, []);

    // 2026-05 决策(用户拍板):本抽屉不持有重算入口,差额不一致时引导用户到"批次管理"页操作。
    const handleJumpToBatchPage = useCallback(() => {
        const targetBatchId = data?.frozen_comparison?.batch_id || batchId;
        if (!targetBatchId) return;
        navigate(`/settlement/batch-calculations/${targetBatchId}`);
    }, [data, batchId, navigate]);

    // T26b — 软删除明细
    const openDeleteConfirm = (record) => {
        setDeleteTarget(record);
        setDeleteReason("");
    };
    const handleConfirmDelete = useCallback(async () => {
        if (!deleteTarget) return;
        const trimmed = (deleteReason || "").trim();
        if (trimmed.length < 5) {
            message.warning("删除原因至少 5 个字符(用于审计追溯)");
            return;
        }
        setDeleting(true);
        try {
            await (deleteTarget.allocation_item_id
                ? settlementApi.dataQuality.removeAllocatedShare(
                    deleteTarget.allocation_item_id,
                    trimmed
                )
                : settlementApi.dataQuality.softDeleteInvoiceLineItem(
                    deleteTarget.invoice_line_item_id,
                    trimmed
                ));
            message.success(
                deleteTarget.allocation_item_id
                    ? "已从本航段去掉这笔分摊，其他飞机不受影响；批次冻结值不变，如需同步请重算批次"
                    : "已删除,实时合计已更新;批次冻结值不变,如需同步请重算批次"
            );
            setDeleteTarget(null);
            setDeleteReason("");
            if (onAfterDelete) onAfterDelete(deleteTarget);
            await loadDetails();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const errorCode = typeof detail === "object" ? detail?.error_code : undefined;
            if (errorCode === "ALREADY_DELETED") {
                message.warning("该明细已经被删除过,无需重复操作");
            } else {
                const msg = typeof detail === "string" ? detail : detail?.message || err.message;
                message.error("删除失败: " + msg);
            }
        } finally {
            setDeleting(false);
        }
    }, [deleteTarget, deleteReason, loadDetails, onAfterDelete]);

    // 顶部对比信息渲染(T24)
    const renderFrozenComparison = () => {
        const fc = data?.frozen_comparison;
        if (!fc) return null;
        const isOk = !fc.needs_recompute;
        return (
            <Alert
                style={{ marginBottom: 12 }}
                type={isOk ? "success" : "warning"}
                showIcon
                message={
                    isOk
                        ? `批次 #${fc.batch_id} 冻结值与实时合计一致`
                        : `批次 #${fc.batch_id} 冻结值与实时合计不一致,差额 ${formatMoney(fc.diff)}`
                }
                description={
                    <div>
                        <Space wrap split={<Divider type="vertical" />}>
                            <span>
                                批次冻结:<Text strong>{formatMoney(fc.frozen_base_total_amount)}</Text>
                            </span>
                            <span>
                                实时合计:<Text strong>{formatMoney(fc.live_base_total_amount)}</Text>
                            </span>
                            <span>
                                差额(实时 - 冻结):
                                <Text strong type={isOk ? undefined : "warning"}>
                                    {fc.diff >= 0 ? "+" : ""}
                                    {formatMoney(fc.diff)}
                                </Text>
                            </span>
                            <span>批次状态:{fc.batch_status}</span>
                        </Space>
                        {!isOk && (
                            <div style={{ marginTop: 8 }}>
                                <Text type="secondary">
                                    若需让批次冻结值跟上实时合计,请到
                                </Text>
                                <Button
                                    type="link"
                                    size="small"
                                    style={{ paddingLeft: 4, paddingRight: 4 }}
                                    onClick={handleJumpToBatchPage}
                                >
                                    批次管理页 #{fc.batch_id}
                                </Button>
                                <Text type="secondary">
                                    点击"刷新批次"操作。
                                </Text>
                            </div>
                        )}
                    </div>
                }
            />
        );
    };

    // 直接 / 分摊 表格列(共用)
    const buildColumns = (isAllocated) => [
        {
            title: "供应商",
            dataIndex: "supplier_name",
            key: "supplier_name",
            width: 160,
            ellipsis: { showTitle: true },
            render: (v) => v || <Text type="secondary">—</Text>,
        },
        {
            title: "账单号",
            dataIndex: "invoice_number",
            key: "invoice_number",
            width: 140,
            ellipsis: true,
            render: (v) => (v ? <Text code>{v}</Text> : <Text type="secondary">—</Text>),
        },
        {
            title: "描述",
            dataIndex: "description",
            key: "description",
            width: 200,
            ellipsis: { showTitle: true },
            render: (v, record) => (
                <Space direction="vertical" size={0}>
                    <span>{v || <Text type="secondary">—</Text>}</span>
                    {record.unrecognized_original_text && (
                        <Tooltip title="该行项目的账单原始费用类型写法未在标准映射表中">
                            <Tag color="orange" style={{ fontSize: 11 }}>
                                原始写法:{record.unrecognized_original_text}
                            </Tag>
                        </Tooltip>
                    )}
                </Space>
            ),
        },
        ...(String(costType || "").toUpperCase() === "ACMI"
            ? [
                  {
                      title: "来源",
                      key: "acmi_source",
                      width: 220,
                      render: (_, record) => {
                          if (!record.acmi_adjustment_id) return <Text type="secondary">原录入</Text>;
                          return (
                              <Space direction="vertical" size={0}>
                                  <Button
                                      type="link"
                                      style={{ padding: 0, height: "auto" }}
                                      disabled={!canOpenAdjustment}
                                      onClick={() => navigate(`/settlement/leg-cost?tab=acmi-adjustment&document=${record.acmi_adjustment_id}`)}
                                  >
                                      {record.acmi_adjustment_period || "调整单据"}
                                  </Button>
                                  {record.got_remainder ? <Tag>含零头</Tag> : null}
                                  {record.acmi_adjustment_reason ? (
                                      <Text type="secondary" style={{ whiteSpace: "pre-wrap" }}>
                                          {record.acmi_adjustment_reason}
                                      </Text>
                                  ) : null}
                              </Space>
                          );
                      },
                  },
              ]
            : []),
        {
            title: isAllocated ? "分摊到本航段" : "金额",
            dataIndex: "base_total_amount",
            key: "base_total_amount",
            width: 140,
            align: "right",
            render: (v, record) => (
                <Space direction="vertical" size={0} style={{ width: "100%" }}>
                    <Text strong style={{ color: "#1677ff" }}>
                        {formatMoney(v)}
                    </Text>
                    {record.original_currency &&
                        record.original_currency !== record.base_currency && (
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                原币:{record.original_currency}{" "}
                                {Number(record.original_total_amount).toLocaleString("en-US", {
                                    minimumFractionDigits: 2,
                                })}
                            </Text>
                        )}
                    {isAllocated && record.full_invoice_base_amount != null && (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            账单全额:{formatMoney(record.full_invoice_base_amount)}
                        </Text>
                    )}
                </Space>
            ),
        },
        {
            title: "类型",
            dataIndex: "allocation_type",
            key: "allocation_type",
            width: 90,
            align: "center",
            render: (v) => (
                <Tag color={ALLOCATION_COLORS[v] || "default"}>{v === "DIRECT" ? "直接" : "分摊"}</Tag>
            ),
        },
        ...(isAllocated
            ? [
                  {
                      title: "分摊规则",
                      dataIndex: "allocation_rule",
                      key: "allocation_rule",
                      width: 120,
                      render: (v) => v || "—",
                  },
              ]
            : []),
        {
            title: "操作",
            key: "actions",
            fixed: "right",
            width: 180,
            render: (_, record) => (
                <Space size="small">
                    <Button
                        size="small"
                        type="link"
                        icon={<FilePdfOutlined />}
                        disabled={!record.has_raw_file}
                        loading={previewing === record.invoice_line_item_id}
                        onClick={() => handlePreviewOriginal(record)}
                    >
                        {record.has_raw_file ? "打开原件" : "原件已丢失"}
                    </Button>
                    {record.acmi_adjustment_id ? (
                        <Text type="secondary">到 ACMI 调整里撤回</Text>
                    ) : (
                        <Button
                            size="small"
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => openDeleteConfirm(record)}
                        >
                            删除该条
                        </Button>
                    )}
                </Space>
            ),
        },
    ];

    const directColumns = useMemo(() => buildColumns(false), [previewing, costType, canOpenAdjustment]); // eslint-disable-line react-hooks/exhaustive-deps
    const allocatedColumns = useMemo(() => buildColumns(true), [previewing, costType, canOpenAdjustment]); // eslint-disable-line react-hooks/exhaustive-deps

    const totalCount = (data?.live_total?.direct_count ?? 0) + (data?.live_total?.allocated_count ?? 0);
    const flightInfo = data?.flight_info;
    const costTypeDisplay = COST_TYPE_DISPLAY[costType] || costType;

    const titleNode = (
        <Space direction="vertical" size={0}>
            <Text strong style={{ fontSize: 16 }}>
                航段费用明细 — {costTypeDisplay}
            </Text>
            {flightInfo && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {flightInfo.flight_number} | {flightInfo.departure_airport} →{" "}
                    {flightInfo.arrival_airport} |{" "}
                    {flightInfo.flight_date ? flightInfo.flight_date.split("T")[0] : "—"}
                </Text>
            )}
        </Space>
    );

    return (
        <Drawer
            title={titleNode}
            placement="right"
            width={"75%"}
            open={visible}
            onClose={onClose}
            destroyOnClose
        >
            {/* S5 / T54 — 看历史档案开关(批次有冻结档案时可用) */}
            {batchId && (
                <div style={{ marginBottom: 12 }}>
                    <Space>
                        <HistoryOutlined />
                        <Text>视图:</Text>
                        <Segmented
                            value={snapshotMode ? "snapshot" : "live"}
                            onChange={(v) => setSnapshotMode(v === "snapshot")}
                            options={[
                                { label: "实时账", value: "live" },
                                { label: `历史档案(批次 #${batchId})`, value: "snapshot" },
                            ]}
                        />
                        {snapshotMode && snapshotVersions.length > 1 && (
                            <>
                                <Text type="secondary">版本:</Text>
                                <Segmented
                                    value={snapshotVersion}
                                    onChange={setSnapshotVersion}
                                    options={snapshotVersions.map((v) => ({
                                        label: `${v.version}(${v.items_count} 条)`,
                                        value: v.version,
                                    }))}
                                />
                            </>
                        )}
                    </Space>
                </div>
            )}

            {/* S5 / T54 — 快照视图(独立于实时账,不混在一起以避免误导) */}
            {snapshotMode && (
                <Spin spinning={snapshotLoading}>
                    {snapshotData ? (
                        <>
                            <Alert
                                type="info"
                                showIcon
                                style={{ marginBottom: 12 }}
                                message={`历史档案 — 批次 #${batchId} ${snapshotData.version || "(无版本)"}`}
                                description={
                                    <Space wrap split={<Divider type="vertical" />}>
                                        <span>
                                            冻结合计:
                                            <Text strong>{formatMoney(snapshotData.total || 0)}</Text>
                                        </span>
                                        <span>明细条数:{snapshotData.items?.length || 0}</span>
                                        <span>
                                            <Text type="secondary">
                                                注:历史档案反映做账瞬间的金额构成。
                                                如发现某条账单显示「已删除」,说明它在做账后被软删除(T26b)。
                                            </Text>
                                        </span>
                                    </Space>
                                }
                            />
                            {snapshotData.items?.length > 0 ? (
                                <Table
                                    rowKey={(r, i) => `${r.invoice_line_item_id || ""}_${r.allocation_item_id || ""}_${i}`}
                                    size="small"
                                    columns={[
                                        {
                                            title: "类型",
                                            dataIndex: "allocation_type",
                                            width: 80,
                                            render: (v) => (
                                                <Tag color={ALLOCATION_COLORS[v]}>
                                                    {v === "DIRECT" ? "直接" : "分摊"}
                                                </Tag>
                                            ),
                                        },
                                        {
                                            title: "供应商",
                                            dataIndex: "supplier_name",
                                            ellipsis: true,
                                            render: (v) => v || <Text type="secondary">—</Text>,
                                        },
                                        {
                                            title: "账单号",
                                            dataIndex: "invoice_number",
                                            width: 140,
                                            ellipsis: true,
                                            render: (v) => (v ? <Text code>{v}</Text> : "—"),
                                        },
                                        {
                                            title: "冻结金额(基准币)",
                                            dataIndex: "frozen_base_amount",
                                            width: 160,
                                            align: "right",
                                            render: (v, r) => (
                                                <Space direction="vertical" size={0} style={{ width: "100%" }}>
                                                    <Text strong style={{ color: "#1677ff" }}>
                                                        {formatMoney(v)}
                                                    </Text>
                                                    {r.frozen_original_amount && (
                                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                                            原币:{r.frozen_original_currency}{" "}
                                                            {r.frozen_original_amount}
                                                        </Text>
                                                    )}
                                                </Space>
                                            ),
                                        },
                                        {
                                            title: "冻结时间",
                                            dataIndex: "frozen_at",
                                            width: 160,
                                            render: (v) => (v ? v.replace("T", " ").slice(0, 19) : "—"),
                                        },
                                    ]}
                                    dataSource={snapshotData.items}
                                    pagination={false}
                                    scroll={{ x: 800 }}
                                />
                            ) : (
                                <Empty description="该(批次、航段、费用类型、版本)无快照数据 — 可能此批次做账时该数字为 0,或在 T52 钩子上线前完成" />
                            )}
                        </>
                    ) : (
                        !snapshotLoading && (
                            <Empty description="尚无历史档案 — 批次可能未触发过 T52 写入(规格 S5 上线前的老批次)" />
                        )
                    )}
                </Spin>
            )}

            {/* 默认实时账视图 */}
            <Spin spinning={loading && !snapshotMode} tip="加载明细中...">
                {/* 顶部统计 + 冻结对比 */}
                {!snapshotMode && data && (
                    <>
                        <Row gutter={16} style={{ marginBottom: 12 }}>
                            <Col span={6}>
                                <Statistic
                                    title="实时合计"
                                    value={formatMoney(data.live_total.base_total_amount)}
                                    prefix={<DollarOutlined />}
                                    valueStyle={{ color: "#1677ff", fontSize: 20 }}
                                />
                            </Col>
                            <Col span={6}>
                                <Statistic
                                    title={
                                        <Space>
                                            直接成本
                                            <Tooltip title="账单行项目直接关联到本航班">
                                                <InfoCircleOutlined style={{ color: "#888" }} />
                                            </Tooltip>
                                        </Space>
                                    }
                                    value={formatMoney(data.live_total.direct_base_total)}
                                    suffix={`(${data.live_total.direct_count} 条)`}
                                    valueStyle={{ fontSize: 18 }}
                                />
                            </Col>
                            <Col span={6}>
                                <Statistic
                                    title={
                                        <Space>
                                            分摊成本
                                            <Tooltip title="账单行项目通过 Allocation 分摊到本航段">
                                                <InfoCircleOutlined style={{ color: "#888" }} />
                                            </Tooltip>
                                        </Space>
                                    }
                                    value={formatMoney(data.live_total.allocated_base_total)}
                                    suffix={`(${data.live_total.allocated_count} 条)`}
                                    valueStyle={{ fontSize: 18 }}
                                />
                            </Col>
                            <Col span={6} style={{ textAlign: "right" }}>
                                <Button icon={<PlusOutlined />} onClick={() => onAddCost && onAddCost(costType)}>
                                    在此录入费用
                                </Button>
                            </Col>
                        </Row>
                        {renderFrozenComparison()}
                    </>
                )}

                {/* T26 — 实时合计为 0 */}
                {!snapshotMode && data && totalCount === 0 && !loading && (
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message={`该航段该费用类型(${costTypeDisplay})尚无账单`}
                        description={
                            <span>
                                如确为漏录,可点击右上方
                                <Button
                                    type="link"
                                    style={{ padding: "0 4px" }}
                                    onClick={() => onAddCost && onAddCost(costType)}
                                >
                                    「在此录入费用」
                                </Button>
                                进行补录。
                            </span>
                        }
                    />
                )}

                {/* 直接成本表 */}
                {!snapshotMode && data && data.direct_items.length > 0 && (
                    <>
                        <Divider orientation="left" plain>
                            <Tag color="blue">直接成本</Tag> 共 {data.direct_items.length} 条
                        </Divider>
                        <Table
                            rowKey="invoice_line_item_id"
                            size="small"
                            columns={directColumns}
                            dataSource={data.direct_items}
                            pagination={false}
                            scroll={{ x: 900 }}
                        />
                    </>
                )}

                {/* 分摊成本表 */}
                {!snapshotMode && data && data.allocated_items.length > 0 && (
                    <>
                        <Divider orientation="left" plain>
                            <Tag color="purple">分摊成本</Tag> 共 {data.allocated_items.length} 条
                        </Divider>
                        <Table
                            rowKey={(r) => `${r.invoice_line_item_id}_${r.allocation_id}`}
                            size="small"
                            columns={allocatedColumns}
                            dataSource={data.allocated_items}
                            pagination={false}
                            scroll={{ x: 1000 }}
                        />
                    </>
                )}

                {!snapshotMode && !loading && data && data.direct_items.length === 0 && data.allocated_items.length === 0 && (
                    <Empty description="该航段该费用类型暂无任何账单明细" />
                )}
            </Spin>

            {/* 软删除二次确认 Modal */}
            <Modal
                title="删除该条账单明细"
                open={!!deleteTarget}
                onCancel={() => {
                    if (!deleting) {
                        setDeleteTarget(null);
                        setDeleteReason("");
                    }
                }}
                onOk={handleConfirmDelete}
                okText="确认删除"
                okButtonProps={{ danger: true, loading: deleting }}
                cancelText="取消"
                cancelButtonProps={{ disabled: deleting }}
                destroyOnClose
            >
                {deleteTarget && (
                    <Space direction="vertical" style={{ width: "100%" }} size="small">
                        <Alert
                            type="warning"
                            showIcon
                            message={
                                deleteTarget.allocation_item_id
                                    ? "只从本航段去掉这笔分摊，其他飞机不受影响"
                                    : "软删除不可见但可恢复(暂无 restore 入口)"
                            }
                            description={
                                <div>
                                    <div>
                                        <Text strong>金额:</Text> {formatMoney(deleteTarget.base_total_amount)}
                                    </div>
                                    <div>
                                        <Text strong>供应商:</Text>{" "}
                                        {deleteTarget.supplier_name || "—"} |{" "}
                                        <Text strong>账单号:</Text>{" "}
                                        {deleteTarget.invoice_number || "—"}
                                    </div>
                                    <div>
                                        <Text strong>分摊类型:</Text>{" "}
                                        <Tag color={ALLOCATION_COLORS[deleteTarget.allocation_type]}>
                                            {deleteTarget.allocation_type}
                                        </Tag>
                                    </div>
                                    {deleteTarget.allocation_item_id && (
                                        <div style={{ marginTop: 4 }}>
                                            <Text type="secondary">
                                                若这是这张单摊到的最后一段，整张分摊单会一并作废。
                                            </Text>
                                        </div>
                                    )}
                                    <div style={{ marginTop: 4 }}>
                                        <Text type="secondary">
                                            删除后实时合计立即更新;批次冻结值不变,如需让批次跟上请使用"立即重算批次"。
                                        </Text>
                                    </div>
                                </div>
                            }
                        />
                        {isBillBackedRecord(deleteTarget) && (
                            <Alert
                                type="info"
                                showIcon
                                message="这条明细来自供应商账单"
                                description={
                                    <span>
                                        来源账单:
                                        <Text strong>
                                            {deleteTarget.invoice_number || deleteTarget.original_filename || "—"}
                                        </Text>
                                        。删除后系统记录将与供应商账单原件不一致(账单仍为已审核状态)。
                                        如果只是这一条录错/识别错,继续删除即可;如果是整张账单有误,
                                        建议改用 AP 对账工作台的「作废并重发」处理整单。
                                    </span>
                                }
                            />
                        )}
                        <div style={{ marginTop: 8 }}>
                            <Text strong style={{ display: "block", marginBottom: 4 }}>
                                删除原因(必填,≥5 字符)
                            </Text>
                            <TextArea
                                value={deleteReason}
                                onChange={(e) => setDeleteReason(e.target.value)}
                                rows={3}
                                placeholder="例如:同账单重复录入,与 INV-001 重复;或 OCR 识别错误,金额单位写错"
                                maxLength={500}
                                showCount
                            />
                        </div>
                    </Space>
                )}
            </Modal>
        </Drawer>
    );
};

export default LegCostDrillDownDrawer;
